/**
 * AI Query Hooks Pattern:
 * All AI query hooks automatically check ff-is-ai-enabled before making requests.
 * This prevents 401 errors and unnecessary API calls when AI features are disabled.
 *
 * useAiSettings is the exception: /v1/ai/settings is gated on the AI OR coaching flag, since
 * Employee Insights ships with Coaching, so it checks the same pair the route does.
 *
 * The pattern uses AND logic with user-provided enabled conditions, so:
 * - If ff-is-ai-enabled is false, query never runs
 * - If ff-is-ai-enabled is true, user's enabled condition applies
 *
 * Note: Mutations (useUpdateAiSettings, setAiSettings) don't need this pattern
 * since they only fire on explicit user actions.
 */
import { UseQueryOptions, UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";

import type { IAIAssistantsSettings } from "@performyard/models";
import type {
	AiQueryRequest,
	AiQueryRequestFrontend,
	AiQueryRequestUrlParams,
	GetCompanyAISettingsResponse,
	PatchCompanyAISettingsRequest,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { companyAISettingsQuery } from "../../queries";
import {
	getAskConversations,
	getHasAccessibleDocuments,
	patchCompanyAISettings,
	postCompanyAISettings,
	postQueryAi,
} from "../../services/defs/internal-api/ai";
import { getStrategyCategories } from "../../services/defs/strategy-category";

type AiQueryRequestProps = AiQueryRequestFrontend &
	AiQueryRequestUrlParams & {
		recreateCount: number;
		enabled?: boolean;
	};

export const useAiQuery = <TResponse>(props: AiQueryRequestProps) => {
	const { type, recreateCount, enabled = true, ...restProps } = props;
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const baseKey = ["ai-query", type, JSON.stringify(restProps)];

	const queryKey = recreateCount > 0 ? [...baseKey, "retry", recreateCount] : baseKey;

	const isEngagementInsight = type === "engagement-insight";
	const isEngagementCohortInsight = type === "engagement-cohort-insights";

	return useQuery<TResponse | UserErrorResponseJson>({
		queryKey: [...queryKey, JSON.stringify(props)],
		queryFn: () => postQueryAi<TResponse>(props as AiQueryRequest & AiQueryRequestUrlParams),
		staleTime: isEngagementInsight || isEngagementCohortInsight ? Infinity : 0,
		refetchOnWindowFocus: false,
		select: (data) => data,
		enabled: ffAiEnabled && enabled,
	});
};

export const useAiSettings = (
	options?: Partial<UseQueryOptions<GetCompanyAISettingsResponse, Error>>
): UseQueryResult<GetCompanyAISettingsResponse, Error> => {
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: ffCoachingEnabled } = useFeatureFlagQuery("ff-is-coaching-enabled", false);

	return useQuery<GetCompanyAISettingsResponse, Error>({
		...companyAISettingsQuery(),
		...options,
		enabled: (ffAiEnabled || ffCoachingEnabled) && (options?.enabled ?? true),
	});
};

export const setAiSettings = () => {
	return useMutation({
		mutationKey: ["ai", "settings"],
		mutationFn: async (aiSettings: IAIAssistantsSettings) => {
			return postCompanyAISettings(aiSettings);
		},
	});
};

export const useHasAccessibleDocuments = () => {
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	return useQuery({
		queryKey: ["ai", "documents", "accessible"],
		queryFn: () => getHasAccessibleDocuments(),
		enabled: ffAiEnabled,
		staleTime: minutesToMilliseconds(5),
	});
};

/**
 * Query key root for the conversation list (PY-13559). Exported so
 * `useAskStream` can invalidate the whole list — including every active search
 * variant — when a turn finishes and reorders it.
 */
export const ASK_CONVERSATIONS_QUERY_KEY = ["ai", "conversations"] as const;

export const useAskConversations = (props: { limit?: number; search?: string; enabled?: boolean }) => {
	const { limit, search, enabled = true } = props;
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const trimmedSearch = search?.trim() ?? "";

	return useQuery({
		queryKey: [...ASK_CONVERSATIONS_QUERY_KEY, "list", { limit, search: trimmedSearch }],
		queryFn: () => getAskConversations({ limit, search: trimmedSearch }),
		enabled: ffAiEnabled && enabled,
		// The list reorders on every turn, so a stale copy is actively misleading
		// about which conversation is most recent.
		staleTime: 0,
		// Typing a search term changes the key, and with staleTime 0 that means a
		// cold fetch per term. Keeping the previous page on screen is what stops
		// the popover collapsing to a spinner between keystrokes.
		placeholderData: (previous) => previous,
	});
};

// There is deliberately no `useAskConversation` detail hook. Both restore paths
// fetch imperatively (AskPyPanelHeader on select, useRestoreActiveConversation on
// open) because the thread enters the Zustand store once and is owned there; a
// query hook keyed on a selected id would re-render the panel on every cache
// touch and re-seed a transcript the user has since added to.

export const useHasGoalCategories = () => {
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	return useQuery({
		queryKey: ["ai", "goal-categories", "has-any"],
		queryFn: async () => {
			const { categories } = await getStrategyCategories();
			return { hasCategories: categories.filter((c) => !c.deleted).length > 0 };
		},
		enabled: ffAiEnabled,
		staleTime: minutesToMilliseconds(5),
	});
};

export const useUpdateAiSettings = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["ai", "settings"],
		mutationFn: async (aiSettings: PatchCompanyAISettingsRequest) => {
			return patchCompanyAISettings(aiSettings);
		},
		onMutate: async (variables) => {
			await queryClient.cancelQueries({ queryKey: ["ai", "settings"] });
			const previousSettings = queryClient.getQueryData(["ai", "settings"]);

			// Optimistically update the query cache with the new values
			queryClient.setQueryData(["ai", "settings"], (old: GetCompanyAISettingsResponse | undefined) => {
				if (!old) return old;
				return { ...old, ...variables };
			});

			return { previousSettings };
		},
		onSuccess: () => {
			// Invalidate the query in the background to ensure consistency (non-blocking)
			queryClient.invalidateQueries({ queryKey: ["ai", "settings"] });
		},
		onError: (_error, _variables, context) => {
			if (context?.previousSettings) {
				queryClient.setQueryData(["ai", "settings"], context.previousSettings);
			}
		},
	});
};
